import { ReactiveController, ReactiveControllerHost } from "lit";
import {
    LoginWithEmailOTPEventEmit,
    LoginWithEmailOTPEventOnReceived,
    Magic,
    PromiEvent,
    MagicRPCError,
    RPCErrorCode,
    MagicUserMetadata,
} from 'magic-sdk'

import { MagicAPI, MagicOptions } from '../base/magic/MagicAPI.js'
import { FanPassUser } from "./types.js";
import { clearStoredSession } from "../utils/helpers.js";

// Extend MagicUserMetadata to include wallets property
interface ExtendedMagicUserMetadata extends MagicUserMetadata {
    wallets?: {
        ethereum?: {
            publicAddress: string;
        };
    };
}

/**
 * The `LoginController` class is responsible for managing the login process using the Magic API.
 * It implements the `ReactiveController` interface to interact with reactive hosts.
 * This class follows the singleton pattern to ensure only one instance is created.
 * 
 * @implements {ReactiveController}
 */
export class LoginController implements ReactiveController {
    private static instance: LoginController;
    private hosts: Set<ReactiveControllerHost> = new Set();
    private debug: boolean = false;
    public callerId: string | null = null;
    public otpHandle:
        | PromiEvent<
              string | null,
              {
                  'email-otp-sent': () => void
                  'invalid-email-otp': () => void
                  'invalid-mfa-otp': () => void
                  'expired-email-otp': () => void
                  'login-throttled': () => void;
                  'mfa-sent-handle': () => void
                  'recovery-code-sent-handle': () => void
                  'invalid-recovery-code': () => void
                  'recovery-code-success': () => void
                  'Auth/id-token-created': (idToken: string) => void
                  'Wallet/wallet-info-fetched': () => void
                  'verify-email-otp': (otp: string) => void
                  'verify-mfa-code': (mfa: string) => void
                  'lost-device': () => void
                  'verify-recovery-code': (recoveryCode: string) => void
                  cancel: () => void
              } & {
                  'device-needs-approval': () => void
                  'device-verification-email-sent': () => void
                  'device-verification-link-expired': () => void
                  'device-approved': () => void
                  'device-retry': () => void
              } & {
                  done: (result: string | null) => void
                  error: (reason: any) => void
                  settled: () => void
                  'closed-by-user': () => void
              }
          >
        | undefined = undefined
    public loginInit: boolean = false;
    public user: FanPassUser | null = null;
    public loggedIn: boolean = false;
    public waitingForOtp: boolean = false;
    public otpError: string | null = null;
    public otpFail: boolean = false;
    public otpCodeRetriesCount: number = 0;
    public magicAPI: Magic | null = null;
    public loggingOut: boolean = false;
    public optinDetails: object | null = null;


    /**
     * Creates an instance of LoginController.
     * If an instance already exists, it returns the existing instance.
     * 
     * @param {MagicOptions} magicOptions - The options to initialize the Magic API.
     * @param {boolean} [debug=false] - Optional debug flag to enable or disable debug mode.
     * @returns {LoginController} The singleton instance of LoginController.
     */    
    constructor(magicOptions: MagicOptions, debug: boolean = false) {
        if (LoginController.instance) {
            return LoginController.instance;
        }
        this.debug = debug;
        this.initMagicAPI(magicOptions)    
    }

    public setOptinDetails(details: object) {
        this.optinDetails = details
    }

    /**
     * Retrieves a DID (Decentralized Identifier) token for the current user.
     * 
     * This method checks if the user is logged in and if a DID token is already available and valid.
     * If the token is expired or not available, it fetches a new token using the `magicAPI`.
     * 
     * @returns {Promise<string | null>} A promise that resolves to the DID token if available, or null if the user is not logged in or the token could not be retrieved.
     */
    public async getDidToken(): Promise<string | null> {
        if (
            this.user
            && this.loggedIn
        ) {
            if (this.user.did && (this.user.did.issueTS + (9 * 60000)) < Date.now()) {
                return this.user.did.token
            }
            const token = await this.magicAPI?.user.getIdToken()
            if (!token) {
                return null
            }
            this.user.did = {
                token,
                issueTS: Date.now()
            }
            return this.user.did.token
        }
        return null
    }

    /**
     * Logs out the current user by calling the `logout` method of the `magicAPI` user.
     * 
     * @returns {Promise<boolean>} A promise that resolves to `true` if the logout was successful, 
     *                             or `false` if an error occurred during the logout process.
     * 
     * @throws {Error} If an error occurs during the logout process.
     */
    public async logout(): Promise<boolean> {
        clearStoredSession()
        this.loggingOut = true
        this.loggedIn = false
        this.user = null
        this.notifyHosts()
        
        try {
            const logout = await this.magicAPI?.user.logout()
            this.callerId = null
            if (logout) {
                this.loggedIn = false
                this.user = null
                this.notifyHosts()
                clearStoredSession()
                return true
            }
        } catch (error) {
            this.loggingOut = false
            return false
        }
        this.loggingOut = false
        return true
    }

    /**
     * Retrieves the user information if the user is logged in.
     * 
     * @returns {Promise<FanPassUser | null>} A promise that resolves to the user information if the user is logged in, or null if the user is not logged in or an error occurs.
     * 
     * @throws Will log an error to the console if there is an issue retrieving the user information.
     */
    public async getUserInfo(): Promise<FanPassUser | null> {
        if (this.user) {
            return this.user
        }
        if (this.loginInit && !this.loggedIn) {
            return null
        }
        try {
            if (!await this.magicAPI?.user.isLoggedIn()) {
                this.loggedIn = false
                return null
            }
            const info = await this.magicAPI?.user.getInfo() as ExtendedMagicUserMetadata    
            if (!info) {
                this.loggedIn = false
                return null
            }
            if (
                info.wallets?.ethereum?.publicAddress
                && info.email
            ) {
                this.loggedIn = true
                this.user = {
                    did: null,
                    walletAddress: info.wallets?.ethereum?.publicAddress || null,
                    email: info.email,
                }
                
                // Get and set the DID token for the user
                /*
                //Should we want to return the DID token to the user?
                try {
                    const token = await this.magicAPI?.user.getIdToken()
                    if (token) {
                        this.user.did = {
                            token,
                            issueTS: Date.now()
                        }
                    }
                } catch (error) {
                    console.error('Error getting DID token', error)
                }
                */
                return this.user
            }
        } catch (error) {
            console.error('Error getting user info', error)
        }
        return null
    }

    /**
     * Cancels the current OTP (One-Time Password) handle if it exists.
     * Emits a 'cancel' event, resets the OTP handle, retries count, error, and failure status.
     *
     * @remarks
     * This method is used to handle the cancellation of the OTP process.
     * It ensures that all related states are reset to their initial values.
     */
    public cancelOtpHandle() {
        if (this.otpHandle) {
            this.otpHandle.emit('cancel')
            this.otpHandle = undefined
            this.otpCodeRetriesCount = 0
            this.otpError = null
            this.otpFail = false
        }
    }

    /**
     * Handles the input of the OTP (One-Time Password) code.
     * 
     * This method is called when the user inputs the OTP code. It increments the retry count,
     * clears any existing OTP error, logs the input code and the OTP handle, and emits an event
     * to verify the email OTP.
     * 
     * @param code - The OTP code input by the user.
     */
    public optCodeInput(code: string) {
        this.otpError = null
        this.logging('optCodeInput', code)
        this.otpHandle?.emit(LoginWithEmailOTPEventEmit.VerifyEmailOtp, code as string);           
        this.otpCodeRetriesCount++
    }

    /**
     * Resends the OTP code to the specified email address.
     * If an OTP handle already exists, it reinitializes the OTP handle with the provided email.
     *
     * @param email - The email address to which the OTP code should be resent.
     */
    public resendOtpCode(email: string) {
        if (this.otpHandle) {
            this.initOtpHandle(email)
        }
    }

    /**
     * Initializes the OTP (One-Time Password) handling process for the given email.
     * Cancels any existing OTP handle, logs the initiation, and sets up the OTP handle
     * with the provided email using the magicAPI's loginWithEmailOTP method.
     * 
     * The OTP handle listens for various events:
     * - `EmailOTPSent`: Sets the waitingForOtp flag to true and notifies hosts.
     * - `InvalidEmailOtp`: Logs the invalid OTP event, sets an error message, and notifies hosts.
     * - `done`: Logs the completion, sets the loggedIn flag to true, retrieves user info, updates the user's DID token, and notifies hosts.
     * - `error`: Logs the error, sets an appropriate error message, sets the otpFail flag to true, and notifies hosts.
     * - `settled`: Logs the settlement of the OTP process.
     * 
     * @param email - The email address to initiate the OTP process for.
     * @returns `false` if the OTP handle could not be created, otherwise void.
     */
    public async initOtpHandle(email: string, callerId: string | null = null): Promise<void> {
        this.cancelOtpHandle()
        this.callerId = callerId
        this.otpHandle = this.magicAPI?.auth.loginWithEmailOTP({
            email,
            showUI: false,
            deviceCheckUI: false,
        })

        const timeout = setTimeout(async () => {
            this.logging('otp timeout');
            this.otpError = 'Magic timed out';
            this.otpFail = true;
            this.notifyHosts();
        }, 60000);

        this.otpHandle?.on(LoginWithEmailOTPEventOnReceived.EmailOTPSent, () => {
                this.waitingForOtp = true
                this.notifyHosts()
            })
            .on(LoginWithEmailOTPEventOnReceived.InvalidEmailOtp, () => {
                clearTimeout(timeout); 
                this.logging('otp InvalidEmailOtp')
                this.otpError = 'Incorrect code. Please try again.'
                this.notifyHosts()
            })
            .on('done', async (result: string | null) => {
                clearTimeout(timeout); 
                this.logging('otp done', result)
                this.loggedIn = true

                //this.hostDispatchEvent()
                await this.getUserInfo()
                if (this.user) {
                    this.user.did = {
                        token: result as string,
                        issueTS: Date.now()
                    }
                }
                this.logging(this.user)
                this.notifyHosts()               
            })
            .on('error', (reason: any) => {
                clearTimeout(timeout); 
                console.error('error', reason)
                this.logging(reason)
                if (
                    reason instanceof MagicRPCError
                    && reason.code === RPCErrorCode.InvalidParams
                ) {
                    this.otpError = 'Invalid email address. Please try again.'
                    this.otpFail = true
                } else {
                    this.otpError = 'Invalid email address. Please try again.'
                    this.otpFail = true
                }

                this.otpFail = true
                this.notifyHosts()
            })
            .on('settled', () => {
                clearTimeout(timeout); 
                this.logging('otp settled')
                // is called when the Promise either resolves or rejects
            }).catch((error: any) => {
                this.logging('otp catch', error)
            })
    }

    /**
     * Initializes the Magic API with the provided options and attempts to retrieve user information.
     * If the user information retrieval is successful or fails, it sets the login initialization flag to true.
     * Finally, it notifies the hosts about the initialization status.
     *
     * @param {MagicOptions} magicOptions - The options to initialize the Magic API.
     * @returns {Promise<void>} A promise that resolves when the initialization and user info retrieval are complete.
     */
    private async initMagicAPI(magicOptions: MagicOptions) {
        this.magicAPI = MagicAPI(magicOptions)
        try {
            if (await this.magicAPI.user.isLoggedIn()) {    
                await this.getUserInfo()        
                this.loginInit = true
            } else {
                this.loginInit = true
            }
        } catch (error) {
            console.error('error', error)
            this.loginInit = true
        }
        this.notifyHosts()
    }

    /**
     * Adds a host to the set of hosts and registers this controller with the host.
     * 
     * @param host - The ReactiveControllerHost instance to be added.
     */
    public addHost(host: ReactiveControllerHost): void {
        if (!this.hosts.has(host)) {
            this.hosts.add(host);
            host.addController(this);
        }
    }


    /**
     * Removes the specified host from the collection of hosts and 
     * removes this controller from the host.
     *
     * @param host - The host to be removed.
     */
    public removeHost(host: ReactiveControllerHost): void {
        if (this.hosts.has(host)) {
            this.hosts.delete(host);
            host.removeController(this);
        }
    }


    /**
     * Notifies all hosts to request an update.
     * 
     * This method iterates over the `hosts` array and calls the `requestUpdate` method
     * on each host to trigger an update.
     * 
     * @private
     */
    private notifyHosts(): void {
        for (const host of this.hosts) {
            host.requestUpdate();
        }
    }

    /*
    private hostDispatchEvent(): void {
        for (const host of this.hosts) {
            (host as EventTarget).dispatchEvent(
                new CustomEvent(LoginEvents.Submit, {
                    bubbles: true,
                    composed: true,
                    detail: {
                        email: 'HELLO',
                    }
                })
            )
        }
    }
    */

    /**
     * Get the singleton instance of the LoginController.
     *
     * @param {MagicOptions} magicOptions - The options to initialize the Magic API.
     * @param {boolean} [debug=false] - Whether to enable debug logging.
     * @returns {LoginController} The singleton instance of the LoginController.
     */
    static getInstance(
        magicOptions?: MagicOptions,
        debug?: boolean
    ): LoginController {
        if (magicOptions === undefined || debug === undefined) {
            throw new Error("First instance must be created with parameters!");
        }
        if (!LoginController.instance) {
            LoginController.instance = new LoginController(magicOptions, debug);
        }
        return LoginController.instance;
    }

    hostConnected(): void {
        // Implementation for hostConnected
    }

    hostDisconnected(): void {
        // Implementation for hostDisconnected
    }

    /**
     * Logs a message to the console if debugging is enabled.
     *
     * @param message - The primary message to log.
     * @param optionalParams - Additional optional parameters to log.
     */
    logging(message?: any, ...optionalParams: any[]): void {
        if (this.debug) {
            console.log(`${message}`, ...optionalParams)
        }
    }    
}



